import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import {
  Building2, ChevronRight, ChevronsLeft, ChevronsRight, FileText, Folder, LayoutDashboard, LayoutGrid, Lock, Search,
  Settings2, Table2, X, Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router';
import { usePermissions } from '@/features/auth/permissions';
import { useBoard } from '@/features/pm/boards/api';
import { useDashboard, useDashboards, useOrganizationDashboards } from '@/features/pm/dashboards/api';
import { useDoc, useDocs, useOrganizationDocs } from '@/features/pm/docs/api';
import {
  organizationName, useOrganization, useOrganizationFolders, useOrganizationWorkspaces, useOrganizations,
} from '@/features/pm/organizations/api';
import { useOrganizationWorkflows, useWorkflow, useWorkspaceWorkflows } from '@/features/pm/workflows/api';
import { useBoards, useFolders, useWorkspaces, type FolderItem } from '@/features/pm/workspaces/api';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/ui.store';

/**
 * Layout for the pages that live inside one workspace - its overview, its
 * boards, docs, dashboards and workflows - or inside an organization: its
 * page, and its own docs, dashboards and workflows. They share a second
 * panel for moving around that place, which belongs with the pages rather
 * than in the main sidebar: listed there, every workspace's boards at once
 * grew without bound and pushed the rest of the navigation off screen.
 */
export function WorkspaceShell() {
  const {
    workspaceId: fromUrl, organizationId: organizationFromUrl,
    boardId = '', docId = '', dashboardId = '', workflowId = '',
  } = useParams();
  // A board, doc, dashboard or workflow page knows its home only through what it
  // shows. The page asks for the same query, so this shares its cache rather
  // than adding a request.
  const { data: board } = useBoard(boardId);
  const { data: doc } = useDoc(docId);
  const { data: dashboard } = useDashboard(dashboardId);
  const { data: workflow } = useWorkflow(workflowId);
  const item = doc ?? dashboard ?? workflow;
  const workspaceId = fromUrl ?? board?.workspaceId ?? item?.workspaceId ?? undefined;
  // An organization's own doc, dashboard or workflow has no workspace.
  const organizationId = organizationFromUrl ?? (item && !item.workspaceId ? item.organizationId : null) ?? undefined;

  return (
    <div className="flex items-start gap-6">
      {organizationId && !workspaceId
        ? <OrganizationPanel organizationId={organizationId} />
        : <WorkspacePanel workspaceId={workspaceId} />}
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

const linkClass = (active: boolean) => cn(
  'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors',
  active ? 'bg-secondary/10 font-semibold text-secondary' : 'text-ink-muted hover:bg-canvas-soft hover:text-ink',
);

const switcherClass = 'h-9 w-full rounded-[var(--radius-control)] border border-line bg-canvas px-2.5 text-[13px] text-ink outline-none focus:ring-2 focus:ring-secondary';

/** Anything the panel lists inside a folder: a board, doc, dashboard, workflow or workspace. */
interface Entry {
  id: string;
  name: string;
  folderId: string | null;
  href: string;
  icon: ComponentType<{ className?: string }>;
  /** Shown with a lock, labelled with this text. */
  privateLabel?: string;
}

function WorkspacePanel({ workspaceId }: { workspaceId: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, workspaceRole } = usePermissions();

  const { data: workspaces } = useWorkspaces();
  const { data: boards, isPending, isError, refetch } = useBoards(workspaceId ?? '');
  // Renaming a doc or changing its privacy refreshes this list, so the tree follows.
  const { data: docs = [] } = useDocs(workspaceId ?? '');
  const { data: dashboards = [] } = useDashboards(workspaceId ?? '');
  const { data: workflows = [] } = useWorkspaceWorkflows(workspaceId ?? '');
  const { data: folders = [] } = useFolders(workspaceId ?? '');
  const workspace = workspaces?.find((w) => w.id === workspaceId);
  // Belonging to the workspace - a membership, not a look around - is what opens its organization.
  const organizationId = workspace?.organizationId
    && (can('pm.organization.manage') || (workspaceId && workspaceRole(workspaceId)))
    ? workspace.organizationId : null;

  // Boards, docs, dashboards, then workflows - the order each folder lists them in.
  const entries = useMemo<Entry[]>(() => [
    ...(boards ?? []).map((b) => ({
      id: b.id, name: b.name, folderId: b.folderId, href: `/boards/${b.id}`, icon: Table2,
      privateLabel: b.isPrivate ? t('boards.private') : undefined,
    })),
    ...docEntries(docs, t),
    ...dashboardEntries(dashboards, t),
    ...workflowEntries(workflows),
  ], [boards, docs, dashboards, workflows, t]);

  return (
    <NavPanel
      title={t('boardNav.panelTitle')}
      searchLabel={t('boardNav.search')}
      switcher={(
        <select aria-label={t('boardNav.switchWorkspace')} value={workspaceId ?? ''}
                onChange={(event) => navigate(`/workspaces/${event.target.value}`)} className={switcherClass}>
          {!workspace && <option value="">{t('common.loading')}</option>}
          {workspaces?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      )}
      links={(
        <>
          <HomeLink />
          {organizationId && (
            <NavLink to={`/organizations/${organizationId}`} end className={({ isActive }) => linkClass(isActive)}>
              <Building2 className="size-4 shrink-0" aria-hidden />{t('boardNav.organization')}
            </NavLink>
          )}
          {workspaceId && (
            <NavLink to={`/workspaces/${workspaceId}`} end className={({ isActive }) => linkClass(isActive)}>
              <Settings2 className="size-4 shrink-0" aria-hidden />{t('boardNav.overview')}
            </NavLink>
          )}
        </>
      )}
      heading={workspace?.name}
      count={boards ? entries.length : workspace?._count.boards ?? ''}
      loading={!workspaceId || isPending}
      error={isError}
      onRetry={() => void refetch()}
      emptyLabel={t('boardNav.empty')}
      entries={entries}
      folders={folders}
    />
  );
}

/** An organization's own level: its folders, workspaces, docs, dashboards and workflows. */
function OrganizationPanel({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const manages = can('pm.organization.manage');

  const { data: organization } = useOrganization(organizationId);
  const { data: organizations } = useOrganizations(manages);
  const { data: workspaces, isPending, isError, refetch } = useOrganizationWorkspaces(organizationId);
  const { data: docs = [] } = useOrganizationDocs(organizationId);
  const { data: dashboards = [] } = useOrganizationDashboards(organizationId);
  const { data: workflows = [] } = useOrganizationWorkflows(organizationId);
  const { data: folders = [] } = useOrganizationFolders(organizationId);

  // Workspaces first, then docs, dashboards and workflows. One the person
  // cannot open (whoever manages organizations sees them all) has no page to link to.
  const entries = useMemo<Entry[]>(() => [
    ...(workspaces ?? []).filter((w) => w.access).map((w) => ({
      id: w.id, name: w.name, folderId: w.folderId, href: `/workspaces/${w.id}`, icon: LayoutGrid,
    })),
    ...docEntries(docs, t),
    ...dashboardEntries(dashboards, t),
    ...workflowEntries(workflows),
  ], [workspaces, docs, dashboards, workflows, t]);

  return (
    <NavPanel
      title={t('boardNav.organizationPanelTitle')}
      searchLabel={t('boardNav.searchOrganization')}
      switcher={manages ? (
        <select aria-label={t('boardNav.switchOrganization')} value={organizationId}
                onChange={(event) => navigate(`/organizations/${event.target.value}`)} className={switcherClass}>
          {!organizations?.some((o) => o.id === organizationId) && (
            <option value={organizationId}>{t('common.loading')}</option>
          )}
          {organizations?.map((o) => <option key={o.id} value={o.id}>{organizationName(o, t)}</option>)}
        </select>
      ) : null}
      links={(
        <>
          <HomeLink />
          <NavLink to={`/organizations/${organizationId}`} end className={({ isActive }) => linkClass(isActive)}>
            <Settings2 className="size-4 shrink-0" aria-hidden />{t('boardNav.organizationOverview')}
          </NavLink>
        </>
      )}
      heading={organization ? organizationName(organization, t) : undefined}
      count={workspaces ? entries.length : ''}
      loading={isPending}
      error={isError}
      onRetry={() => void refetch()}
      emptyLabel={t('boardNav.organizationEmpty')}
      entries={entries}
      folders={folders}
    />
  );
}

/** Where `/` leads: the organizations for whoever manages them, otherwise their workspaces. */
function HomeLink() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  return (
    <NavLink to="/" end className={({ isActive }) => linkClass(isActive)}>
      <LayoutGrid className="size-4 shrink-0" aria-hidden />
      {t(can('pm.organization.manage') ? 'boardNav.allOrganizations' : 'boardNav.allWorkspaces')}
    </NavLink>
  );
}

type Translate = (key: string) => string;
type Listed = { id: string; name: string; folderId: string | null };

const docEntries = (docs: (Listed & { isPrivate: boolean })[], t: Translate) => docs.map((d): Entry => ({
  id: d.id, name: d.name, folderId: d.folderId, href: `/docs/${d.id}`, icon: FileText,
  privateLabel: d.isPrivate ? t('docs.private') : undefined,
}));

const dashboardEntries = (dashboards: (Listed & { isPrivate: boolean })[], t: Translate) =>
  dashboards.map((d): Entry => ({
    id: d.id, name: d.name, folderId: d.folderId, href: `/dashboards/${d.id}`, icon: LayoutDashboard,
    privateLabel: d.isPrivate ? t('dashboards.private') : undefined,
  }));

const workflowEntries = (workflows: Listed[]) => workflows.map((w): Entry => ({
  id: w.id, name: w.name, folderId: w.folderId, href: `/workflows/${w.id}`, icon: Zap,
}));

/** The panel itself: a switcher, a search, a few links and the folder tree of what lives there. */
function NavPanel({
  title, searchLabel, switcher, links, heading, count, loading, error, onRetry, emptyLabel, entries, folders,
}: {
  title: string;
  searchLabel: string;
  switcher: ReactNode;
  links: ReactNode;
  heading: string | undefined;
  count: number | string;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  emptyLabel: string;
  entries: Entry[];
  folders: FolderItem[];
}) {
  const { t } = useTranslation();
  const collapsed = useUiStore((s) => s.workspacePanelCollapsed);
  const toggle = useUiStore((s) => s.toggleWorkspacePanel);
  const [search, setSearch] = useState('');

  // Below md the main sidebar is already an overlay; a second column would
  // leave the board no room, and the Workspaces page covers the same ground.
  if (collapsed) {
    return (
      <aside className="sticky top-0 hidden shrink-0 md:block">
        <button onClick={toggle} aria-label={t('boardNav.expandPanel')} title={t('boardNav.expandPanel')}
                className="rounded-lg border border-line bg-surface p-2 text-ink-muted hover:bg-canvas-soft hover:text-ink">
          <ChevronsRight className="size-4 rtl:rotate-180" aria-hidden />
        </button>
      </aside>
    );
  }

  const query = search.trim().toLocaleLowerCase();
  const matching = entries.filter((e) => e.name.toLocaleLowerCase().includes(query));

  return (
    <aside aria-label={title}
           className="sticky top-0 hidden max-h-[calc(100vh-9rem)] w-[260px] shrink-0 flex-col gap-3 overflow-hidden rounded-[var(--radius-shell)] border border-line bg-surface p-3 md:flex">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <button onClick={toggle} aria-label={t('boardNav.collapsePanel')} title={t('boardNav.collapsePanel')}
                className="rounded-md p-1 text-ink-muted hover:bg-canvas-soft hover:text-ink">
          <ChevronsLeft className="size-4 rtl:rotate-180" aria-hidden />
        </button>
      </div>

      {switcher}

      <div className="flex items-center gap-2 rounded-[var(--radius-control)] border border-line bg-canvas px-2.5 focus-within:ring-2 focus-within:ring-secondary">
        <Search className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />
        <input aria-label={searchLabel} placeholder={searchLabel} value={search}
               onChange={(event) => setSearch(event.target.value)}
               className="h-9 min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-subtle" />
        {search && (
          <button onClick={() => setSearch('')} aria-label={t('boardNav.clear')} className="text-ink-subtle hover:text-ink">
            <X className="size-3.5" aria-hidden />
          </button>
        )}
      </div>

      <nav className="flex flex-col gap-0.5">{links}</nav>

      <div className="flex min-h-0 flex-col border-t border-line pt-3">
        <p className="flex items-center justify-between px-2.5 pb-1.5 text-xs font-semibold text-ink">
          <span className="truncate">{heading}</span>
          <span className="tabular-nums text-ink-subtle">{count}</span>
        </p>
        <div className="min-h-0 overflow-y-auto">
          {loading ? <p role="status" className="px-2.5 py-2 text-xs text-ink-subtle">{t('common.loading')}</p>
            : error ? (
              <button onClick={onRetry} className="px-2.5 py-2 text-xs text-ink-muted hover:text-ink">
                {t('boardNav.error')} · {t('common.retry')}
              </button>
            )
            : !matching.length ? <p className="px-2.5 py-2 text-xs text-ink-subtle">{query ? t('boardNav.noMatches') : emptyLabel}</p>
            // A search shows the matches flat; otherwise the folders, as on the page.
            : query ? matching.map((entry) => <EntryLink key={entry.id} entry={entry} />)
            : <FolderLevel parentId={null} folders={folders} entries={matching} />}
        </div>
      </div>
    </aside>
  );
}

function EntryLink({ entry, depth = 0 }: { entry: Entry; depth?: number }) {
  const Icon = entry.icon;
  return (
    <NavLink to={entry.href} title={entry.name}
             style={{ paddingInlineStart: `${0.625 + depth * 0.875}rem` }}
             className={({ isActive }) => cn(linkClass(isActive), 'my-0.5')}>
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{entry.name}</span>
      {entry.privateLabel && <Lock className="ms-auto size-3 shrink-0 opacity-60" aria-label={entry.privateLabel} />}
    </NavLink>
  );
}

/** One level of the tree: its folders first, then what sits directly in it. */
function FolderLevel({ parentId, folders, entries, depth = 0 }: {
  parentId: string | null; folders: FolderItem[]; entries: Entry[]; depth?: number;
}) {
  // A folder hidden from this person leaves its contents at the top level.
  const known = new Set(folders.map((f) => f.id));
  const parentOf = (id: string | null) => (id && known.has(id) ? id : null);
  return (
    <>
      {folders.filter((f) => parentOf(f.parentId) === parentId).map((folder) => (
        <FolderBranch key={folder.id} folder={folder} folders={folders} entries={entries} depth={depth} />
      ))}
      {entries.filter((e) => parentOf(e.folderId) === parentId).map((entry) => (
        <EntryLink key={entry.id} entry={entry} depth={depth} />
      ))}
    </>
  );
}

function FolderBranch({ folder, folders, entries, depth }: {
  folder: FolderItem; folders: FolderItem[]; entries: Entry[]; depth: number;
}) {
  const { boardId, docId, dashboardId, workflowId } = useParams();
  const onScreen = boardId ?? docId ?? dashboardId ?? workflowId;
  // Open by default when what is on screen is somewhere inside.
  const holdsCurrent = (id: string): boolean =>
    entries.some((e) => e.folderId === id && e.id === onScreen)
    || folders.some((f) => f.parentId === id && holdsCurrent(f.id));
  const [open, setOpen] = useState(() => holdsCurrent(folder.id));

  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open}
              style={{ paddingInlineStart: `${0.625 + depth * 0.875}rem` }}
              className={cn(linkClass(false), 'my-0.5 w-full')}>
        <ChevronRight className={cn('size-3 shrink-0 transition-transform rtl:rotate-180', open && 'rotate-90 rtl:rotate-90')} aria-hidden />
        <Folder className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{folder.name}</span>
      </button>
      {open && <FolderLevel parentId={folder.id} folders={folders} entries={entries} depth={depth + 1} />}
    </div>
  );
}
